import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Col, Container, Row, Button, Spinner } from 'reactstrap';
import CommonTableIndexWise from '../../../../Components/Common/CommonTableIndexWise';
import { useGlobalStudy } from 'context/StudyContext';
import { useReportsSummaryService } from 'modules/Pharmacy/Reports/api/useReportsSummary';
import { reportsSummaryApiService } from 'modules/Pharmacy/Reports/api/reportsSummary.api';
import CommonSearch from 'Components/Common/CommonSearch';
import StatCard from '../../../../Components/Common/StatCard';
import XLSX from 'xlsx-js-style';
import { formatDate, GetUserData } from 'common/service/CommonService';
import StockLedgerFilterModal, { StockLedgerFilterValues } from '../components/StockLedgerFilterModal';
import sf from '../../../../assets/scss/common/searchFilter.module.scss';
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from 'common/constants/PermissionConstants';
import { BreadcrumbContext } from 'Layouts/Header';
import { REPORTS_PATHS } from 'Routes/paths';

const StockLedgerReport = () => {
    const breadcrumbContext = useContext(BreadcrumbContext);

    useEffect(() => {
        breadcrumbContext?.setBreadcrumbItems?.([
            { text: "Pharmacy Reports", link: REPORTS_PATHS.SUMMARY },
            { text: "Stock Ledger" }
        ]);
        return () => breadcrumbContext?.setBreadcrumbItems?.([]);
    }, []);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [searchText, setSearchText] = useState<string>("");
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState<StockLedgerFilterValues>({});
    const [isFilterApplied, setIsFilterApplied] = useState(false);

    const { selectedStudy: globalStudy } = useGlobalStudy();
    const [selectedStudy, setSelectedStudy] = useState<string>("");

    useEffect(() => {
        if (globalStudy?.studyid) {
            setSelectedStudy(String(globalStudy.studyid));
        }
        else if (globalStudy === null) {
            setSelectedStudy("");
        }
    }, [globalStudy]);

    // API binding using React Query
    const { SelectLedgerQuery } = useReportsSummaryService();
    const ledgerQuery = SelectLedgerQuery({
        studyid: selectedStudy ? Number(selectedStudy) : null,
        transactiontype: filters?.transactiontype || null,
        fromdate: filters?.fromDate || null,
        todate: filters?.toDate || null,
        searchtext: searchText || null,
        ipcategory: filters?.ipcategory || null,
        currentpage: currentPage,
        pagesize: itemsPerPage,
        pagination: true,
    });

    const apiData = ledgerQuery.data?.data || [];

    // Dynamic stats computation from response payload
    const stats = useMemo(() => {
        if (!apiData || apiData.length === 0) {
            return {
                totalCount: 0,
                totalAdded: 0,
                totalRemoved: 0,
                totalReturned: 0
            };
        }

        const firstItem = apiData[0];

        return {
            totalCount: firstItem.total_count || 0,
            totalAdded: firstItem.totalin || 0,
            totalRemoved: firstItem.totalout || 0,
            totalReturned: firstItem.totalreturn || 0
        };
    }, [apiData]);

    const columns: any = [
        {
            key: 'studycode',
            label: 'Study Code',
            render: (val: any) => <span className="text-muted fw-semibold fs-13">{val || '-'}</span>
        },
        {
            key: 'ipcategory',
            label: 'IP Category',
            render: (val: any) => {
                const isTestProduct =
                    val?.toLowerCase() === 'test product';

                return (
                    <span
                        className="px-2 py-1 fs-11 rounded fw-semibold"
                        style={{
                            background: isTestProduct
                                ? 'rgba(124, 58, 237, 0.10)'
                                : 'rgba(15, 118, 110, 0.10)',

                            color: isTestProduct
                                ? '#7c3aed'
                                : '#0f766e',

                            border: isTestProduct
                                ? '1px solid rgba(124, 58, 237, 0.18)'
                                : '1px solid rgba(15, 118, 110, 0.18)',

                            letterSpacing: '0.2px'
                        }}
                    >
                        {val || '-'}
                    </span>
                );
            }
        },
        {
            key: 'productname',
            label: 'Product Name',
            render: (val: any) => <span className="text-dark fw-bold fs-13">{val || '-'}</span>
        },
        {
            key: 'batchlotno',
            label: 'Batch Lot No',
            render: (val: any) => <span className="badge bg-light text-muted border px-2 py-1 fs-12">{val || '-'}</span>
        },
        {
            key: 'transactiontype',
            label: 'Transaction Type',
            render: (val: any) => {

                const type = val?.toUpperCase() || '';

                let styles = {
                    background: 'rgba(59, 130, 246, 0.10)',
                    color: '#2563eb',
                    border: '1px solid rgba(59, 130, 246, 0.16)'
                };

                if (type === 'ADD' || type === 'RETURN') {
                    styles = {
                        background: 'rgba(22, 163, 74, 0.10)',
                        color: '#15803d',
                        border: '1px solid rgba(22, 163, 74, 0.16)'
                    };
                }

                else if (
                    type === 'DISPENSE' ||
                    type === 'DISPOSAL'
                ) {
                    styles = {
                        background: 'rgba(220, 38, 38, 0.08)',
                        color: '#dc2626',
                        border: '1px solid rgba(220, 38, 38, 0.14)'
                    };
                }

                else if (type === 'TRANSFER') {
                    styles = {
                        background: 'rgba(245, 158, 11, 0.10)',
                        color: '#d97706',
                        border: '1px solid rgba(245, 158, 11, 0.16)'
                    };
                }

                else if (type === 'RETENTION') {
                    styles = {
                        background: 'rgba(6, 182, 212, 0.10)',
                        color: '#0891b2',
                        border: '1px solid rgba(6, 182, 212, 0.16)'
                    };
                }

                else if (type === 'OUT') {
                    styles = {
                        background: 'rgba(124, 58, 237, 0.10)',
                        color: '#7c3aed',
                        border: '1px solid rgba(124, 58, 237, 0.16)'
                    };
                }

                else if (type === 'IN') {
                    styles = {
                        background: 'rgba(14, 165, 233, 0.10)',
                        color: '#0284c7',
                        border: '1px solid rgba(14, 165, 233, 0.16)'
                    };
                }

                return (
                    <span
                        className="px-2 py-1 fs-11 rounded fw-semibold"
                        style={{
                            ...styles,
                            letterSpacing: '0.2px'
                        }}
                    >
                        {val || '-'}
                    </span>
                );
            }
        },
        {
            key: 'transactiondate',
            label: 'Transaction Date',
            render: (val: any) => <span className="text-muted fw-semibold fs-13">{formatDate(val) || '-'}</span>
        },
        {
            key: 'stocksource',
            label: 'Stock Source',
            render: (val: any) => {
                const normalized = val?.toLowerCase() || 'actual stock';

                let styles = {
                    background: 'rgba(100, 116, 139, 0.10)',
                    color: '#475569',
                    border: '1px solid rgba(100, 116, 139, 0.16)'
                };

                if (normalized === 'retention stock') {
                    styles = {
                        background: 'rgba(245, 158, 11, 0.10)',
                        color: '#d97706',
                        border: '1px solid rgba(245, 158, 11, 0.16)'
                    };
                } else if (normalized === 'returned stock') {
                    styles = {
                        background: 'rgba(22, 163, 74, 0.10)',
                        color: '#15803d',
                        border: '1px solid rgba(22, 163, 74, 0.16)'
                    };
                } else if (normalized === 'actual stock') {
                    styles = {
                        background: 'rgba(59, 130, 246, 0.10)',
                        color: '#2563eb',
                        border: '1px solid rgba(59, 130, 246, 0.16)'
                    };
                }

                return (
                    <span
                        className="px-2 py-1 fs-11 rounded fw-semibold "
                        style={{
                            ...styles,
                            letterSpacing: '0.2px'
                        }}
                    >
                        {val || 'Actual Stock'}
                    </span>
                );
            }
        },
        {
            key: 'quantity',
            label: 'Quantity',
            render: (val: any) => <span className="text-dark fw-bold fs-13">{val ?? 0}</span>
        },
    ];

    const onSearch = (text: string | null) => {
        setSearchText(text ?? "");
        setCurrentPage(1);
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const res = await reportsSummaryApiService.getLedgerReport({
                studyid: selectedStudy ? Number(selectedStudy) : null,
                transactiontype: filters?.transactiontype || null,
                fromdate: filters?.fromDate || null,
                todate: filters?.toDate || null,
                searchtext: searchText || null,
                ipcategory: filters?.ipcategory || null,
                pagination: false,
            });

            const exportData = res?.data || [];
            const fileName = `Stock_Ledger_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;

            const headerRow = [
                "Study Code",
                "IP Category",
                "Product Name",
                "Batch / Lot No",
                "Transaction Type",
                "Transaction Date",
                "Stock Source",
                "Quantity"
            ];
            const user = GetUserData();

            const dataRows = exportData.map((item: any) => [
                item.studycode || "-",
                item.ipcategory || "-",
                item.productname || "-",
                item.batchlotno || "-",
                item.transactiontype || "-",
                formatDate(item.transactiondate) || "-",
                item.stocksource || "Actual Stock",
                Number(item.quantity) ?? 0
            ]);

            const titleRow = ["STOCK LEDGER REPORT", "", "", "", "", "", "📒", ""];
            const subtitle = ["Detailed record of pharmacy inventory additions, dispensings, disposals, and transfers", "", "", "", "", "", "", ""];
            const generatedRow = [`Generated On: ${new Date().toLocaleString()} | Generated By: ${user?.userdisplayname || 'System User'}`, "", "", "", "", "", "", ""];

            const allRows = [
                [""], // Row 1: Spacer
                titleRow, // Row 2
                subtitle, // Row 3
                generatedRow, // Row 4
                [""], // Row 5: Spacer between generated metadata and table headers
                headerRow, // Row 6
                ...dataRows,
                [""], // Spacer Row before warning note
                [`🛡️ Note: Figures are system generated. Please verify before taking action.`, "", "", "", "", "", "", ""] // Footer warning row
            ];

            const ws = XLSX.utils.aoa_to_sheet(allRows);

            // Core Premium Palette Styles (Segoe UI Font Family)
            const titleStyle = {
                font: { name: "Segoe UI", sz: 18, bold: true, color: { rgb: "064E3B" } }, // Dark green
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "FFFFFF" } }
            };

            const subtitleStyle = {
                font: { name: "Segoe UI", sz: 10, italic: true, color: { rgb: "047857" } }, // Medium green
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "FFFFFF" } }
            };

            const metaStyle = {
                font: { name: "Segoe UI", sz: 9.5, color: { rgb: "4B5563" } }, // Cool gray
                alignment: { vertical: "center", horizontal: "center" }, // Center aligned!
                fill: { fgColor: { rgb: "FFFFFF" } }
            };

            const iconStyle = {
                font: { name: "Segoe UI Emoji", sz: 32 }, // Segoe UI Emoji renders ledger 📒 beautifully
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "EBF7EE" } } // Premium mint-green card background
            };

            const headerStyle = {
                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "064E3B" } }, // Deep forest green header
                alignment: { vertical: "center", horizontal: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "043E2B" } },
                    bottom: { style: "thin", color: { rgb: "043E2B" } },
                    left: { style: "thin", color: { rgb: "043E2B" } },
                    right: { style: "thin", color: { rgb: "043E2B" } }
                }
            };

            const cellBorder = {
                top: { style: "thin", color: { rgb: "E2E8F0" } },
                bottom: { style: "thin", color: { rgb: "E2E8F0" } },
                left: { style: "thin", color: { rgb: "E2E8F0" } },
                right: { style: "thin", color: { rgb: "E2E8F0" } }
            };

            const textStyle = {
                font: { name: "Segoe UI", sz: 10, color: { rgb: "1F2937" } },
                alignment: { vertical: "center", horizontal: "center" },
                border: cellBorder
            };

            const numberStyle = {
                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: "1F2937" } },
                alignment: { vertical: "center", horizontal: "center" },
                border: cellBorder
            };

            const footerStyle = {
                font: { name: "Segoe UI", sz: 12, bold: true, color: { rgb: "064E3B" } }, // Dark green
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "EBF7EE" } } // Super light mint green
            };

            const getIpCategoryStyle = (val: string, rowFill: any) => {
                const isTestProduct = val?.toLowerCase() === 'test product';
                return {
                    font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: isTestProduct ? "7C3AED" : "0F766E" } },
                    fill: rowFill,
                    alignment: { vertical: "center", horizontal: "center" },
                    border: cellBorder
                };
            };

            // Custom Merge Rules
            ws["!merges"] = [
                // Title (A2:F2)
                { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
                // Subtitle (A3:F3)
                { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
                // Meta info (A4:F4)
                { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
                // Icon (G2:H4)
                { s: { r: 1, c: 6 }, e: { r: 3, c: 7 } },
                // Warning note at the end (A_last:H_last)
                { s: { r: 6 + exportData.length + 1, c: 0 }, e: { r: 6 + exportData.length + 1, c: 7 } }
            ];

            const getIconBorder = (r: number, c: number) => {
                return {
                    top: r === 1 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                    bottom: r === 3 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                    left: c === 6 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                    right: c === 7 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined
                };
            };

            const getFooterBorder = (c: number) => {
                const border: any = {
                    top: { style: "thin", color: { rgb: "A5D6A7" } },
                    bottom: { style: "thin", color: { rgb: "A5D6A7" } }
                };
                if (c === 0) border.left = { style: "thin", color: { rgb: "A5D6A7" } };
                if (c === 7) border.right = { style: "thin", color: { rgb: "A5D6A7" } };
                return border;
            };

            const range = XLSX.utils.decode_range(ws['!ref'] || "");
            for (let r = range.s.r; r <= range.e.r; ++r) {
                const isAlternate = r % 2 === 0;
                const rowFill = isAlternate ? { fgColor: { rgb: "F5F8F6" } } : { fgColor: { rgb: "FFFFFF" } };

                for (let c = range.s.c; c <= range.e.c; ++c) {
                    const cellRef = XLSX.utils.encode_cell({ r, c });
                    if (!ws[cellRef]) continue;

                    if (r === 0) {
                        ws[cellRef].s = {
                            fill: { fgColor: { rgb: "FFFFFF" } }
                        };
                    } else if (r === 1) {
                        if (c <= 5) {
                            ws[cellRef].s = titleStyle;
                        } else {
                            ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                        }
                    } else if (r === 2) {
                        if (c <= 5) {
                            ws[cellRef].s = subtitleStyle;
                        } else {
                            ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                        }
                    } else if (r === 3) {
                        if (c <= 5) {
                            ws[cellRef].s = metaStyle;
                        } else {
                            ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                        }
                    } else if (r === 4) {
                        ws[cellRef].s = {
                            fill: { fgColor: { rgb: "FFFFFF" } }
                        };
                    } else if (r === 5) {
                        ws[cellRef].s = headerStyle;
                    } else if (r > 5 && r < 6 + exportData.length) {
                        // Data Rows
                        if (c === 7) {
                            // Quantity
                            ws[cellRef].s = {
                                ...numberStyle,
                                fill: rowFill
                            };
                        } else if (c === 6) {
                            // Stock Source - text color only based on value
                            const srcVal = ws[cellRef].v?.toString() || '';
                            const normalized = srcVal?.toLowerCase();
                            let textColor = "475569"; // default slate
                            if (normalized === 'retention stock') {
                                textColor = "B45309"; // amber
                            } else if (normalized === 'returned stock') {
                                textColor = "047857"; // green
                            } else if (normalized === 'actual stock') {
                                textColor = "1D4ED8"; // blue
                            }
                            ws[cellRef].s = {
                                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: textColor } },
                                fill: rowFill,
                                alignment: { vertical: "center", horizontal: "center" },
                                border: cellBorder
                            };
                        } else if (c === 1) {
                            // IP Category - colored text only
                            const ipVal = ws[cellRef].v?.toString() || '';
                            ws[cellRef].s = getIpCategoryStyle(ipVal, rowFill);
                        } else if (c === 4) {
                            // Transaction Type badge styles
                            const valText = ws[cellRef].v?.toString() || '';
                            const type = valText.toUpperCase();
                            let badgeBg = "F1F5F9";
                            let badgeText = "475569";

                            if (type === 'ADD' || type === 'RETURN') {
                                badgeBg = "D1FAE5"; // Soft green
                                badgeText = "047857"; // Dark green
                            } else if (type === 'DISPENSE' || type === 'DISPOSAL') {
                                badgeBg = "FEE2E2"; // Soft red
                                badgeText = "B91C1C"; // Dark red
                            } else if (type === 'TRANSFER') {
                                badgeBg = "FEF3C7"; // Soft yellow
                                badgeText = "B45309"; // Dark yellow
                            } else if (type === 'RETENTION') {
                                badgeBg = "CFFAFE"; // Soft cyan
                                badgeText = "0E7490"; // Dark cyan
                            } else if (type === 'OUT') {
                                badgeBg = "EDE9FE"; // Soft purple
                                badgeText = "6D28D9"; // Dark purple
                            } else if (type === 'IN') {
                                badgeBg = "E0F2FE"; // Soft sky
                                badgeText = "0369A1"; // Dark sky
                            }

                            ws[cellRef].s = {
                                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: badgeText } },
                                fill: rowFill,
                                alignment: { vertical: "center", horizontal: "center" },
                                border: cellBorder
                            };
                        } else {
                            ws[cellRef].s = {
                                ...textStyle,
                                fill: rowFill
                            };
                        }
                    } else if (r === 6 + exportData.length) {
                        // Spacer before footer
                        ws[cellRef].s = {
                            fill: { fgColor: { rgb: "FFFFFF" } }
                        };
                    } else if (r === 6 + exportData.length + 1) {
                        // Footer warning row
                        ws[cellRef].s = {
                            ...footerStyle,
                            border: getFooterBorder(c)
                        };
                    }
                }
            }

            // Apply column widths
            ws['!cols'] = [
                { wch: 16 }, // Study Code
                { wch: 18 }, // IP Category
                { wch: 18 }, // Product Name
                { wch: 16 }, // Batch Lot No
                { wch: 20 }, // Transaction Type
                { wch: 18 }, // Transaction Date
                { wch: 18 }, // Stock Source
                { wch: 14 }  // Quantity
            ];

            // Row Heights
            ws['!rows'] = [
                { hpt: 20 }, // Spacer Row 1
                { hpt: 30 }, // Title Row 2
                { hpt: 22 }, // Subtitle Row 3
                { hpt: 22 }, // Generated Row 4
                { hpt: 20 }, // Spacer Row 5
                { hpt: 26 }  // Header Row 6
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Stock Ledger");
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setIsExporting(false);
        }
    };

    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const applyFilter = (filterVals: StockLedgerFilterValues) => {
        setFilters(filterVals);
        setIsFilterApplied(true);
        setCurrentPage(1);
    };

    const clearFilter = () => {
        setFilters({});
        setIsFilterApplied(false);
        setCurrentPage(1);
    };

    return (
        <div className="page-content">
            <Container fluid className="px-4">
                <div className="bg-white p-4 rounded shadow-sm mb-4 border border-light">
                    {/* Screen / Summary Cards */}
                    <Row className="mb-4 g-3 px-1">
                        <Col lg={3} md={6}>
                            <StatCard
                                title="TOTAL ENTRIES"
                                value={stats.totalCount}
                                subtext="all ledger log records"
                                icon={<i className="ri-list-check-2" />}
                                bgColor="#f5f3ff"
                                iconColor="#7c3aed"
                                trendType="info"
                                subtextIcon="ri-group-line"
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <StatCard
                                title="TOTAL INWARD"
                                value={stats.totalAdded}
                                subtext="total quantity inward"
                                icon={<i className="ri-download-line" />}
                                bgColor="#ecfdf5"
                                iconColor="#059669"
                                trendType="success"
                                subtextIcon="ri-arrow-left-down-line"
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <StatCard
                                title="TOTAL OUTWARD"
                                value={stats.totalRemoved}
                                subtext="total quantity outward"
                                icon={<i className="ri-upload-line" />}
                                bgColor="#fffbeb"
                                iconColor="#d97706"
                                trendType="negative"
                                subtextIcon="ri-arrow-right-up-line"
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <StatCard
                                title="TOTAL RETURNED"
                                value={stats.totalReturned}
                                subtext="total quantity returned"
                                icon={<i className="ri-text-wrap" />}
                                bgColor="#eff6ff"
                                iconColor="#2563eb"
                                trendType="info"
                                subtextIcon="ri-arrow-go-back-line"
                            />
                        </Col>
                    </Row>

                    {/* Table Section Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4 mt-5">
                        <div>
                            <h5 className="mb-1 text-dark fw-bold fs-16">Stock Ledger Report</h5>
                            <p className="text-muted mb-0 fs-13">Showing {stats.totalCount} records</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className={sf.wrapper}>
                                <CommonSearch onSearch={onSearch} searchText={searchText} />
                                <button
                                    type="button"
                                    className={`${sf.filterBtn} ${isFilterApplied ? sf.active : ""}`}
                                    onClick={toggleFilter}
                                    title="Filters"
                                >
                                    <i className="ri-filter-3-line" style={{ fontSize: 15 }} />
                                    {isFilterApplied && <span className={sf.filterDot}></span>}
                                </button>
                            </div>
                            <Button
                                color="light"
                                className="d-flex align-items-center gap-2 text-dark fs-13 border border-light-subtle px-3 py-2"
                                style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}
                                onClick={handleExport}
                                disabled={isExporting}
                            >
                                {isExporting ? (
                                    <Spinner size="sm" className="me-1" />
                                ) : (
                                    <i className="ri-file-excel-2-line text-success fs-15"></i>
                                )}
                                Export Excel
                            </Button>
                        </div>
                    </div>

                    <div className="table-responsive mt-3">
                        <CommonTableIndexWise
                            data={apiData}
                            columns={columns}
                            loading={ledgerQuery.isLoading || ledgerQuery.isFetching}
                            totalRecords={apiData?.[0]?.total_count}
                            currentPage={currentPage}
                            pageSize={itemsPerPage}
                            onPageChange={setCurrentPage}
                            hideAction={true}
                        />
                    </div>
                </div>
            </Container>

            <StockLedgerFilterModal
                isOpen={isFilterOpen}
                toggle={toggleFilter}
                onApply={applyFilter}
                onClear={clearFilter}
                existingFilters={filters}
            />
        </div>
    );
};

export default StockLedgerReport;
